import { ChangeDetectorRef, Component, OnInit, ViewChild, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged, filter, finalize, take } from 'rxjs';
import { AuthService } from '../../auth/services/auth.service';
import { AuthUserResponse } from '../../auth/models/user.model';
import { SharedModule } from 'src/shared/shared.module';
import { LabelMode } from 'src/utils/label-mode.enum';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { CommonModule } from '@angular/common';
import { ScheduleSubmissionsService } from 'src/app/services/api/schedule-submissions/schedule-submissions.service';
import { TeachCompensationService } from 'src/app/services/api/teach-compensation/teach-compensation.service';
import {
  MakeupTeachingRequestCreatePayload,
  MakeupTeachingRequestCreationContext,
} from 'src/app/services/models/teach-compensation/teach-compensation.model';
import {
  MakeupTeachingAcademicHolidayOption,
  MakeupTeachingDropdownOptionsResponse,
  MakeupTeachingDropdownOptionsParams,
  MakeupTeachingDropdownRules,
  MakeupTeachingLeavePeriodOption,
  MakeupTeachingLeaveTypeOption,
  MakeupTeachingSubjectScheduleGroupOption,
  MakeupTeachingTimeOption,
  ProjectResearchSubjectBlockModel,
  ScheduleSubmissionBlockModel,
  ScheduleSubmissionsTableModel,
  SubjectScheduleBlockModel,
} from 'src/app/services/models/schedule/schedule-submissions.model';

interface HolidayOption {
  id: number;
  name: string;
  holidayDate: string;
}

interface SelectOption {
  id: number;
  name: string;
  timeValue?: string | null;
}

interface TeacherNameSource {
  full_name: string;
  academic_rank?: string | null;
}

interface LeaveTypeSelectOption extends MakeupTeachingLeaveTypeOption {
  name: string;
}

interface LeavePeriodSelectOption extends MakeupTeachingLeavePeriodOption {
  name: string;
}

interface SubjectScheduleSelectOption extends MakeupTeachingSubjectScheduleGroupOption {
  optionLabel: string;
}

type LeaveDateFieldMode = 'manual' | 'holiday' | 'empty';

interface TimetableHour {
  label: string;
  colSpan: number;
}

interface TimetableDay {
  dayOfWeek: number;
  thaiName: string;
  englishName: string;
}

interface TimetableEvent {
  startSlot: number;
  endSlot: number;
  subjectLabel: string;
  subjectName: string;
  detailLabel: string;
}

interface TimetableCell {
  colSpan: number;
  events: TimetableEvent[];
}

interface TimetableRow extends TimetableDay {
  cells: TimetableCell[];
}

const TIMETABLE_START_HOUR = 7;
const TIMETABLE_END_HOUR = 21;
const TIMETABLE_SLOT_MINUTES = 30;
const TIMETABLE_SLOTS_PER_HOUR = 60 / TIMETABLE_SLOT_MINUTES;
const TIMETABLE_TOTAL_SLOTS = ((TIMETABLE_END_HOUR - TIMETABLE_START_HOUR) * 60) / TIMETABLE_SLOT_MINUTES;
const DEFAULT_MINIMUM_MAKEUP_MINUTES = 60;
const MAKEUP_REQUEST_LEAD_DAYS = 3;

const TIMETABLE_HOURS: TimetableHour[] = Array.from(
  { length: TIMETABLE_END_HOUR - TIMETABLE_START_HOUR },
  (_, index) => {
    const hour = TIMETABLE_START_HOUR + index;

    return {
      label: `${hour}:00-${hour + 1}:00`,
      colSpan: 60 / TIMETABLE_SLOT_MINUTES,
    };
  }
);

const TIMETABLE_DAYS: TimetableDay[] = [
  { dayOfWeek: 1, thaiName: 'วันจันทร์', englishName: 'Monday' },
  { dayOfWeek: 2, thaiName: 'วันอังคาร', englishName: 'Tuesday' },
  { dayOfWeek: 3, thaiName: 'วันพุธ', englishName: 'Wednesday' },
  { dayOfWeek: 4, thaiName: 'วันพฤหัสบดี', englishName: 'Thursday' },
  { dayOfWeek: 5, thaiName: 'วันศุกร์', englishName: 'Friday' },
  { dayOfWeek: 6, thaiName: 'วันเสาร์', englishName: 'Saturday' },
  { dayOfWeek: 7, thaiName: 'วันอาทิตย์', englishName: 'Sunday' },
];

@Component({
  selector: 'app-teach-compensation-details',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './teach-compensation-details.component.html',
  styleUrls: ['./teach-compensation-details.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TeachCompensationDetailsComponent implements OnInit {
  @ViewChild('submissionConfirmDialog')
  private submissionConfirmDialog?: ConfirmDialogComponent;

  @ViewChild('submissionErrorDialog')
  private submissionErrorDialog?: ConfirmDialogComponent;

  @ViewChild('submissionSuccessDialog')
  private submissionSuccessDialog?: ConfirmDialogComponent;

  readonly LabelMode = LabelMode;
  readonly authService = inject(AuthService);
  readonly currentTeacherName = computed(() => this.formatTeacherName(this.authService.currentUser()?.user));
  readonly compensatoryTeachingTime = signal<SelectOption[]>([]);
  readonly subjectScheduleOptions = signal<SubjectScheduleSelectOption[]>([]);

  readonly holidays = signal<HolidayOption[]>([]);
  readonly leaveTypeOptions = signal<LeaveTypeSelectOption[]>([]);
  readonly leaveDateFieldMode = signal<LeaveDateFieldMode>('empty');

  readonly timeRangeOptions = signal<LeavePeriodSelectOption[]>([]);
  readonly dropdownRules = signal<MakeupTeachingDropdownRules | null>(null);
  readonly timetableHours = TIMETABLE_HOURS;
  readonly timetableRows = signal<TimetableRow[]>(this.buildTimetableRows([]));
  readonly isSubmitting = signal(false);
  readonly hasSubmitted = signal(false);
  readonly leaveMinSelectableDate = signal<string | null>(null);
  readonly minSelectableDate = signal<string | null>(null);
  readonly maxSelectableDate = signal<string | null>(null);
  readonly scheduleSubmissionLoadError = signal('');

  readonly academicTermName = signal('');
  readonly academicYear = signal('');
  readonly hasAcademicTermPreview = computed(
    () => this.academicTermName() !== '' && this.academicYear() !== '',
  );
  submissionErrorMessage = '';

  form = inject(FormBuilder).group({
    leaveType: [null as number | null, Validators.required],
    leaveDate: [{ value: null as string | null, disabled: true }, Validators.required],
    holidayId: [{ value: null as number | null, disabled: true }, Validators.required],
    timeRange: [{ value: null as number | null, disabled: true }, Validators.required],
    subjectId: [{ value: null as number | null, disabled: true }, Validators.required],
    subjectCode: [{ value: '', disabled: true }],
    section: [{ value: '', disabled: true }],
    compensationDate: [
      { value: null as string | null, disabled: true },
      [Validators.required, this.makeupDateLeadTimeValidator()],
    ],
    compensationStartTime: [{ value: null as number | null, disabled: true }, Validators.required],
    compensationEndTime: [{ value: null as number | null, disabled: true }, Validators.required],
  }, {
    validators: this.makeupTimeRangeValidator(),
  });

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly scheduleSubmissionService = inject(ScheduleSubmissionsService);
  private readonly teachCompensationService = inject(TeachCompensationService);
  private readonly cdr = inject(ChangeDetectorRef);

  private academicTermId?: number;
  private scheduleSubmissionId?: number;
  private dropdownOptionsRequestId = 0;
  private lastLeaveContextDropdownOptionsKey = '';

  get selectedLeaveTypeId(): number | null {
    return this.form.get('leaveType')?.value ?? null;
  }

  get requiresAcademicHoliday(): boolean {
    return this.dropdownRules()?.requires_academic_holiday === true;
  }

  get requiresManualLeaveDate(): boolean {
    return this.dropdownRules()?.requires_manual_leave_date === true;
  }

  get availableTimeRangeOptions(): LeavePeriodSelectOption[] {
    const forcedLeavePeriodCode = this.dropdownRules()?.force_leave_period_code;

    if (forcedLeavePeriodCode == null) {
      return this.timeRangeOptions();
    }

    return this.timeRangeOptions().filter((item) => item.code === forcedLeavePeriodCode);
  }

  get isTimeRangeForced(): boolean {
    return this.dropdownRules()?.force_leave_period_code != null;
  }

  get previewLeaveType(): string {
    const leaveTypeId = this.form.getRawValue().leaveType;
    return this.leaveTypeOptions().find((item) => item.id === leaveTypeId)?.name ?? '-';
  }

  get previewLeaveDateLabel(): string {
    return this.requiresAcademicHoliday ? 'วันหยุด' : 'วันที่ลา';
  }

  get previewLeaveDate(): string {
    const formValue = this.form.getRawValue();

    if (this.requiresAcademicHoliday) {
      return this.holidays().find((item) => item.id === formValue.holidayId)?.name ?? '-';
    }

    return this.formatBuddhistDate(this.toApiDate(formValue.leaveDate) ?? '');
  }

  get previewLeavePeriod(): string {
    const timeRangeId = this.form.getRawValue().timeRange;
    return this.availableTimeRangeOptions.find((item) => item.id === timeRangeId)?.name ?? '-';
  }

  get previewSubjectName(): string {
    const subjectId = this.form.getRawValue().subjectId;
    return this.subjectScheduleOptions().find((item) => item.id === subjectId)?.name ?? '-';
  }

  get previewSubjectCode(): string {
    const subjectCode = this.form.getRawValue().subjectCode?.trim();
    return subjectCode != null && subjectCode !== '' ? subjectCode : '-';
  }

  get previewCompensationDate(): string {
    return this.formatBuddhistDate(this.toApiDate(this.form.getRawValue().compensationDate) ?? '');
  }

  get previewCompensationTime(): string {
    const formValue = this.form.getRawValue();
    const startTime = this.getMakeupTimeLabel(formValue.compensationStartTime);
    const endTime = this.getMakeupTimeLabel(formValue.compensationEndTime);

    return startTime === '-' || endTime === '-' ? '-' : `${startTime}–${endTime} น.`;
  }

  get previewCompensationDuration(): string {
    const formValue = this.form.getRawValue();
    const startMinutes = this.getMakeupTimeMinutes(formValue.compensationStartTime);
    const endMinutes = this.getMakeupTimeMinutes(formValue.compensationEndTime);

    if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
      return '-';
    }

    const durationMinutes = endMinutes - startMinutes;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (hours === 0) {
      return `${minutes} น.`;
    }

    if (minutes === 0) {
      return `${hours} ชม.`;
    }

    return `${hours} ชม. ${minutes} น.`;
  }

  ngOnInit(): void {
    this.listenLeaveType();
    this.listenLeaveContext();

    const currentUser = this.authService.currentUser();

    if (currentUser != null) {
      this.initializeFromSession(currentUser);
      return;
    }

    this.authService.currentUser$
      .pipe(
        filter((user): user is AuthUserResponse => user != null),
        take(1),
      )
      .subscribe((user) => this.initializeFromSession(user));
  }

  onSubjectChange(subject: SubjectScheduleSelectOption | undefined): void {
    if (subject == null) {
      this.form.patchValue({
        subjectCode: '',
        section: '',
      });
      return;
    }

    this.form.patchValue({
      subjectCode: subject.code,
      section: subject.section ?? '',
    });
  }

  onCancel(): void {
    this.navigateToList();
  }

  onSave(): void {
    this.hasSubmitted.set(true);

    if (this.form.invalid || this.scheduleSubmissionId == null) {
      this.form.markAllAsTouched();
      return;
    }

    this.submissionConfirmDialog?.open();
  }

  onConfirmSubmission(): void {
    if (this.isSubmitting()) {
      return;
    }

    if (this.form.invalid || this.scheduleSubmissionId == null) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.buildCreatePayload();

    if (payload == null) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    this.teachCompensationService.createMakeupTeachingRequest(payload)
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.submissionConfirmDialog?.close();
          this.submissionSuccessDialog?.open();
        },
        error: (err) => {
          this.submissionConfirmDialog?.close();
          this.openSubmissionErrorDialog(this.getSubmissionErrorMessage(err));
        },
      });
  }

  onCloseSubmissionSuccess(): void {
    this.submissionSuccessDialog?.close();
    this.navigateToList();
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);

    return control != null &&
      control.enabled &&
      control.invalid &&
      (this.hasSubmitted() || control.touched)
  }

  isCompensationDateInvalid(): boolean {
    const control = this.form.get('compensationDate');

    return control != null &&
      control.enabled &&
      control.invalid &&
      this.hasSubmitted();
  }

  getCompensationDateErrorMessage(): string {
    const control = this.form.get('compensationDate');
    const minDate = this.minSelectableDate();

    if (control?.hasError('makeupDateTooEarly') !== true || minDate == null) {
      return 'กรุณาเลือกวันที่ขอสอนชดเชย';
    }

    const message = `กรุณาเลือกวันที่ขอสอนชดเชยตั้งแต่วันที่ ${this.formatBuddhistDate(minDate)} เป็นต้นไป`;

    return this.isLeadTimeTheEarliestMakeupDate()
      ? `${message} (ต้องขอล่วงหน้าอย่างน้อย ${MAKEUP_REQUEST_LEAD_DAYS} วัน)`
      : message;
  }

  // ขอบล่างมาจากวันเริ่มเรียนของเทอมได้เช่นกัน จึงบอกกติกาล่วงหน้า 3 วันเฉพาะตอนที่กติกานั้นเป็นตัวกำหนด
  private isLeadTimeTheEarliestMakeupDate(): boolean {
    const minDate = this.parseLocalDate(this.minSelectableDate());

    return minDate?.getTime() === this.earliestMakeupDate().getTime();
  }

  isCompensationTimeInvalid(controlName: 'compensationStartTime' | 'compensationEndTime'): boolean {
    return this.isInvalid(controlName) ||
      this.isMakeupTimeRangeInvalid() ||
      this.isMakeupTimeMinimumDurationInvalid();
  }

  getCompensationTimeErrorMessage(controlName: 'compensationStartTime' | 'compensationEndTime'): string {
    if (this.isMakeupTimeRangeInvalid()) {
      return 'กรุณาเลือกเวลาสิ้นสุดให้มากกว่าเวลาเริ่มต้น';
    }

    if (this.isMakeupTimeMinimumDurationInvalid()) {
      return 'กรุณาเลือกเวลาสอนชดเชยอย่างน้อย 1 ชั่วโมง';
    }

    return controlName === 'compensationStartTime'
      ? 'กรุณาเลือกเวลาเริ่มสอนชดเชย'
      : 'กรุณาเลือกเวลาสิ้นสุดสอนชดเชย';
  }

  private initializeFromSession(user: AuthUserResponse): void {
    const routeAcademicTermValue = this.route.snapshot.queryParamMap.get('academic_term_id');
    const routeAcademicTermId = Number(routeAcademicTermValue);
    const sessionAcademicTermId = Number(user.academic_term?.id);
    const academicTermId = routeAcademicTermValue == null
      ? sessionAcademicTermId
      : routeAcademicTermId;

    if (!Number.isFinite(academicTermId) || academicTermId <= 0) {
      this.scheduleSubmissionLoadError.set('ไม่พบข้อมูลเทอมการศึกษาสำหรับสร้างคำขอสอนชดเชย');
      return;
    }

    this.academicTermId = academicTermId;
    this.loadCreationContext(academicTermId);
  }

  private loadCreationContext(academicTermId: number): void {
    this.scheduleSubmissionLoadError.set('');
    this.teachCompensationService
      .getMakeupTeachingRequestCreationContext(academicTermId)
      .pipe(take(1))
      .subscribe({
        next: (context) => this.applyCreationContext(context),
        error: (err) => {
          console.error(err);
          this.scheduleSubmissionId = undefined;
          this.scheduleSubmissionLoadError.set('ไม่สามารถโหลดข้อมูลสำหรับสร้างคำขอสอนชดเชยได้');
        },
      });
  }

  private applyCreationContext(context: MakeupTeachingRequestCreationContext): void {
    const academicTerm = context.academic_term;
    this.academicTermId = academicTerm.id;
    this.academicTermName.set(academicTerm.name ?? '');
    this.academicYear.set(String(this.toBuddhistYear(academicTerm.year)));
    this.setSelectableDateRange(academicTerm);

    if (!context.can_create || context.schedule_submission_id == null) {
      this.scheduleSubmissionId = undefined;
      this.scheduleSubmissionLoadError.set(
        context.message ?? 'ไม่สามารถสร้างคำขอสอนชดเชยสำหรับเทอมการศึกษาที่เลือกได้',
      );
      return;
    }

    this.scheduleSubmissionId = context.schedule_submission_id;
    this.loadInitialDropdownOptions(context.schedule_submission_id);
    this.loadInitialSchecduleTabel(context.schedule_submission_id);
  }

  private navigateToList(): void {
    const commands = ['/teach-compensation-management/teach-compensation'];
    if (this.academicTermId == null) {
      void this.router.navigate(commands);
      return;
    }

    void this.router.navigate(commands, {
      queryParams: { academic_term_id: this.academicTermId },
    });
  }

  private formatTeacherName(user: TeacherNameSource | null | undefined): string {
    const fullName = user?.full_name.trim() ?? '';
    const academicRank = String(user?.academic_rank ?? '').trim();

    if (fullName === '') {
      return '-';
    }

    return academicRank === '' ? fullName : `${academicRank} ${fullName}`;
  }

  private isMakeupTimeRangeInvalid(): boolean {
    const startControl = this.form.get('compensationStartTime');
    const endControl = this.form.get('compensationEndTime');
    const hasTouchedTime =
      startControl?.dirty === true ||
      startControl?.touched === true ||
      endControl?.dirty === true ||
      endControl?.touched === true;

    return this.form.hasError('makeupTimeRange') && hasTouchedTime;
  }

  private isMakeupTimeMinimumDurationInvalid(): boolean {
    const startControl = this.form.get('compensationStartTime');
    const endControl = this.form.get('compensationEndTime');
    const hasTouchedTime =
      startControl?.dirty === true ||
      startControl?.touched === true ||
      endControl?.dirty === true ||
      endControl?.touched === true;

    return this.form.hasError('makeupTimeMinimumDuration') && hasTouchedTime;
  }

  private makeupDateLeadTimeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const selectedDate = this.parseLocalDate(this.toApiDate(control.value));
      const minDate = this.parseLocalDate(this.minSelectableDate());

      if (selectedDate == null || minDate == null) {
        return null;
      }

      return selectedDate.getTime() < minDate.getTime() ? { makeupDateTooEarly: true } : null;
    };
  }

  private makeupTimeRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const startTimeId = control.get('compensationStartTime')?.value;
      const endTimeId = control.get('compensationEndTime')?.value;

      if (startTimeId == null || endTimeId == null) {
        return null;
      }

      const startMinutes = this.getMakeupTimeMinutes(startTimeId);
      const endMinutes = this.getMakeupTimeMinutes(endTimeId);

      if (startMinutes == null || endMinutes == null) {
        const startOrder = this.getMakeupTimeOrder(startTimeId);
        const endOrder = this.getMakeupTimeOrder(endTimeId);

        if (startOrder != null && endOrder != null) {
          if (endOrder <= startOrder) {
            return { makeupTimeRange: true };
          }

          const minimumStepCount = this.minimumMakeupMinutes() / TIMETABLE_SLOT_MINUTES;
          return endOrder - startOrder < minimumStepCount
            ? { makeupTimeMinimumDuration: true }
            : null;
        }

        const startNumber = Number(startTimeId);
        const endNumber = Number(endTimeId);

        if (!Number.isFinite(startNumber) || !Number.isFinite(endNumber)) {
          return null;
        }

        if (endNumber <= startNumber) {
          return { makeupTimeRange: true };
        }

        return null;
      }

      if (endMinutes <= startMinutes) {
        return { makeupTimeRange: true };
      }

      return endMinutes - startMinutes < this.minimumMakeupMinutes()
        ? { makeupTimeMinimumDuration: true }
        : null;
    };
  }

  private getMakeupTimeOrder(timeId: unknown): number | null {
    const numericTimeId = Number(timeId);

    if (!Number.isFinite(numericTimeId)) {
      return null;
    }

    const index = this.compensatoryTeachingTime().findIndex((item) => item.id === numericTimeId);
    return index >= 0 ? index : null;
  }

  private getMakeupTimeMinutes(timeId: unknown): number | null {
    const numericTimeId = Number(timeId);

    if (!Number.isFinite(numericTimeId)) {
      return null;
    }

    const option = this.compensatoryTeachingTime().find((item) => item.id === numericTimeId);
    const parsedMinutes = [option?.timeValue, option?.name]
      .map((value) => this.parseTimeMinutes(value))
      .find((value): value is number => value != null);

    if (parsedMinutes != null) {
      return parsedMinutes;
    }

    const order = this.getMakeupTimeOrder(timeId);
    return order == null ? null : order * TIMETABLE_SLOT_MINUTES;
  }

  private parseTimeMinutes(value: string | null | undefined): number | null {
    const match = /(\d{1,2}):(\d{2})/.exec(value ?? '');

    if (match == null) {
      return null;
    }

    const hour = Number(match[1]);
    const minute = Number(match[2]);

    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      return null;
    }

    return (hour * 60) + minute;
  }

  private getMakeupTimeLabel(timeId: unknown): string {
    const numericTimeId = Number(timeId);

    if (!Number.isFinite(numericTimeId)) {
      return '-';
    }

    return this.compensatoryTeachingTime().find((item) => item.id === numericTimeId)?.name ?? '-';
  }

  private minimumMakeupMinutes(): number {
    const ruleMinutes = this.dropdownRules()?.minimum_makeup_minutes;
    return ruleMinutes != null && Number.isFinite(ruleMinutes) && ruleMinutes > 0
      ? ruleMinutes
      : DEFAULT_MINIMUM_MAKEUP_MINUTES;
  }

  private openSubmissionErrorDialog(message: string): void {
    this.submissionErrorMessage = message;
    this.cdr.detectChanges();
    this.submissionErrorDialog?.open();
  }

  private getSubmissionErrorMessage(error: unknown): string {
    const fallbackMessage = 'ไม่สามารถสร้างคำขอสอนชดเชยได้ กรุณาลองใหม่อีกครั้ง';
    const responseError = error instanceof HttpErrorResponse
      ? error.error
      : (error as { error?: unknown } | null | undefined)?.error;
    const parsedResponseError = this.parseErrorResponse(responseError);

    if (parsedResponseError != null && typeof parsedResponseError === 'object') {
      const errorRecord = parsedResponseError as { errors?: unknown; error?: unknown; message?: unknown };

      if (Array.isArray(errorRecord.errors) && errorRecord.errors.length > 0) {
        const errors = errorRecord.errors
          .filter((item): item is string => typeof item === 'string' && item.trim() !== '')
          .map((item) => item.trim());

        if (errors.length > 0) {
          return errors.join('\n');
        }
      }

      const errorText = this.getErrorText(errorRecord.error);
      const messageText = this.getErrorText(errorRecord.message);
      const message = errorText !== '' ? errorText : messageText;
      return message !== '' ? message : fallbackMessage;
    }

    const responseErrorText = this.getErrorText(parsedResponseError);
    const messageText = this.getErrorText((error as { message?: unknown } | null | undefined)?.message);
    const message = responseErrorText !== '' ? responseErrorText : messageText;

    return message !== '' ? message : fallbackMessage;
  }

  private parseErrorResponse(value: unknown): unknown {
    if (typeof value !== 'string') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private getErrorText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private listenLeaveType(): void {
    this.form.get('leaveType')?.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((leaveType) => {
        this.dropdownOptionsRequestId += 1;
        this.lastLeaveContextDropdownOptionsKey = '';
        this.resetDependentFields();
        this.resetDependentValidationState();
        this.subjectScheduleOptions.set([]);
        this.dropdownRules.set(null);
        this.setLeaveDateFieldModeForLeaveType(leaveType);

        if (leaveType == null) {
          this.disableDependentFields();
          this.clearDependentOptions();
          return;
        }

        this.enablePrimaryFieldsForLeaveType();

        if (this.scheduleSubmissionId == null) {
          return;
        }

        this.loadDropdownOptionsForLeaveType(this.scheduleSubmissionId, leaveType);
      });
  }

  private listenLeaveContext(): void {
    [
      'leaveDate',
      'holidayId',
      'timeRange',
    ].forEach((controlName) => {
      this.form.get(controlName)?.valueChanges.subscribe(() => this.reloadDropdownOptionsForLeaveContext());
    });
  }

  private resetDependentValidationState(): void {
    [
      'leaveDate',
      'holidayId',
      'timeRange',
      'subjectId',
      'subjectCode',
      'section',
      'compensationDate',
      'compensationStartTime',
      'compensationEndTime',
    ].forEach((controlName) => {
      const control = this.form.get(controlName);

      control?.markAsPristine();
      control?.markAsUntouched();
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private resetLeaveTypeDependentInteractionState(): void {
    [
      'leaveDate',
      'holidayId',
      'timeRange',
    ].forEach((controlName) => {
      const control = this.form.get(controlName);

      control?.markAsPristine();
      control?.markAsUntouched();
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private resetDependentFields(): void {
    this.form.patchValue({
      leaveDate: null,
      holidayId: null,
      timeRange: null,
      subjectId: null,
      subjectCode: '',
      section: '',
      compensationDate: null,
      compensationStartTime: null,
      compensationEndTime: null,
    }, { emitEvent: false });
  }

  private resetSubjectContextFields(): void {
    this.form.patchValue({
      subjectId: null,
      subjectCode: '',
      section: '',
    }, { emitEvent: false });
  }

  private resetSubjectContextValidationState(): void {
    [
      'subjectId',
      'subjectCode',
      'section',
    ].forEach((controlName) => {
      const control = this.form.get(controlName);

      control?.markAsPristine();
      control?.markAsUntouched();
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private disableDependentFields(): void {
    [
      'leaveDate',
      'holidayId',
      'timeRange',
      'subjectId',
      'subjectCode',
      'section',
      'compensationDate',
      'compensationStartTime',
      'compensationEndTime',
    ].forEach((controlName) => this.form.get(controlName)?.disable({ emitEvent: false }));
  }

  private enablePrimaryFieldsForLeaveType(): void {
    this.form.get('timeRange')?.enable({ emitEvent: false });
    this.form.get('subjectId')?.enable({ emitEvent: false });
    this.form.get('compensationDate')?.enable({ emitEvent: false });
    this.form.get('compensationStartTime')?.enable({ emitEvent: false });
    this.form.get('compensationEndTime')?.enable({ emitEvent: false });

    if (this.leaveDateFieldMode() === 'holiday') {
      this.form.get('holidayId')?.enable({ emitEvent: false });
      this.form.get('leaveDate')?.disable({ emitEvent: false });
      return;
    }

    if (this.leaveDateFieldMode() === 'manual') {
      this.form.get('leaveDate')?.enable({ emitEvent: false });
      this.form.get('holidayId')?.disable({ emitEvent: false });
    }
  }

  private enableControlsForRules(rules: MakeupTeachingDropdownRules): void {
    this.form.get('timeRange')?.enable({ emitEvent: false });
    this.form.get('subjectId')?.enable({ emitEvent: false });
    this.form.get('compensationDate')?.enable({ emitEvent: false });
    this.form.get('compensationStartTime')?.enable({ emitEvent: false });
    this.form.get('compensationEndTime')?.enable({ emitEvent: false });

    if (rules.requires_academic_holiday) {
      this.form.get('holidayId')?.enable({ emitEvent: false });
      this.form.get('leaveDate')?.disable({ emitEvent: false });
    }

    if (rules.requires_manual_leave_date) {
      this.form.get('leaveDate')?.enable({ emitEvent: false });
      this.form.get('holidayId')?.disable({ emitEvent: false });
    }
  }

  private clearDependentOptions(): void {
    this.holidays.set([]);
    this.timeRangeOptions.set([]);
    this.compensatoryTeachingTime.set([]);
    this.subjectScheduleOptions.set([]);
  }

  private toHolidayOption(holiday: MakeupTeachingAcademicHolidayOption): HolidayOption {
    const holidayDate = holiday.holiday_date ?? holiday.start_date ?? '';
    const holidayName = holiday.name ?? holiday.label ?? '';

    return {
      id: holiday.id,
      name: holidayDate === ''
        ? holidayName
        : `${holidayName} (${this.formatBuddhistDate(holidayDate)})`,
      holidayDate,
    };
  }

  private formatBuddhistDate(value: string): string {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear() + 543;

    return `${day}/${month}/${year}`;
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private buildCreatePayload(): MakeupTeachingRequestCreatePayload | null {
    if (this.scheduleSubmissionId == null) {
      return null;
    }

    const formValue = this.form.getRawValue();
    const leaveTypeId = formValue.leaveType;
    const leavePeriodId = formValue.timeRange;
    const subjectId = formValue.subjectId;
    const makeupTeachingDate = this.toApiDate(formValue.compensationDate);
    const makeupStartTimeId = formValue.compensationStartTime;
    const makeupEndTimeId = formValue.compensationEndTime;

    if (
      leaveTypeId == null ||
      leavePeriodId == null ||
      subjectId == null ||
      makeupTeachingDate == null ||
      makeupStartTimeId == null ||
      makeupEndTimeId == null
    ) {
      return null;
    }

    const makeupTeachingRequest = {
      schedule_submission_id: this.scheduleSubmissionId,
      makeup_teaching_leave_type_id: leaveTypeId,
      makeup_teaching_leave_period_id: leavePeriodId,
      subject_id: subjectId,
      makeup_teaching_date: makeupTeachingDate,
      makeup_start_time_id: makeupStartTimeId,
      makeup_end_time_id: makeupEndTimeId,
    };

    if (this.requiresAcademicHoliday) {
      if (formValue.holidayId == null) {
        return null;
      }

      return {
        makeup_teaching_request: {
          ...makeupTeachingRequest,
          academic_holiday_id: formValue.holidayId,
        },
      };
    }

    if (this.requiresManualLeaveDate) {
      const leaveDate = this.toApiDate(formValue.leaveDate);

      if (leaveDate == null) {
        return null;
      }

      return {
        makeup_teaching_request: {
          ...makeupTeachingRequest,
          leave_date: leaveDate,
        },
      };
    }

    return {
      makeup_teaching_request: makeupTeachingRequest,
    };
  }

  private toApiDate(value: string | null): string | null {
    if (value == null || value === '') {
      return null;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private loadInitialDropdownOptions(scheduleSubmissionId: number): void {
    this.scheduleSubmissionService
      .getMakeupTeachingDropdownOptions(scheduleSubmissionId)
      .subscribe({
        next: (dropdownOptions) => {
          this.applyDropdownOptions(dropdownOptions, true);
        },
        error: (err) => console.error(err),
      });
  }
  private loadInitialSchecduleTabel(scheduleSubmissionId: number): void {
    this.scheduleSubmissionService
      .getScheduleSubmissionTabel(scheduleSubmissionId)
      .subscribe({
        next: (res) => {
          this.timetableRows.set(this.buildTimetableRows(res.blocks));
        },
        error: (err) => console.error(err),
      });
  }

  private buildTimetableRows(blocks: ScheduleSubmissionsTableModel['blocks']): TimetableRow[] {
    const eventsByDay = this.toTimetableEvents(blocks).reduce<Record<number, TimetableEvent[]>>(
      (acc, event) => {
        (acc[event.dayOfWeek] ??= []).push(event);
        return acc;
      },
      {}
    );

    return TIMETABLE_DAYS.map((day) => ({
      ...day,
      cells: this.buildTimetableCells(eventsByDay[day.dayOfWeek] ?? []),
    }));
  }

  private toTimetableEvents(blocks: ScheduleSubmissionBlockModel[]): (TimetableEvent & Pick<TimetableDay, 'dayOfWeek'>)[] {
    return blocks.flatMap((block) => {
      if (block.type === 'subject_schedule') {
        return this.toSubjectScheduleTimetableEvent(block);
      }

      return this.toProjectResearchTimetableEvents(block);
    });
  }

  private toSubjectScheduleTimetableEvent(
    block: SubjectScheduleBlockModel
  ): (TimetableEvent & Pick<TimetableDay, 'dayOfWeek'>)[] {
    const event = this.createTimetableEvent({
      dayOfWeek: block.time.day_of_week,
      startTime: block.time.start_time,
      endTime: block.time.end_time,
      subjectLabel: this.formatSubjectLabel(block.subject.code, block.section),
      subjectName: this.formatSubjectTooltip(block.subject.name, block.subject.code, block.section),
      detailLabel: block.time.room_name ?? '',
    });

    return event == null ? [] : [event];
  }

  private toProjectResearchTimetableEvents(
    block: ProjectResearchSubjectBlockModel
  ): (TimetableEvent & Pick<TimetableDay, 'dayOfWeek'>)[] {
    if (!block.show_in_timetable || block.display_area !== 'timetable') {
      return [];
    }

    return block.teaching_times.flatMap((time) => {
      const event = this.createTimetableEvent({
        dayOfWeek: time.day_of_week,
        startTime: time.start_time,
        endTime: time.end_time,
        subjectLabel: this.formatSubjectLabel(block.subject.code, block.section),
        subjectName: this.formatSubjectTooltip(block.subject.name, block.subject.code, block.section),
        detailLabel: '',
      });

      return event == null ? [] : [event];
    });
  }

  private createTimetableEvent(params: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    subjectLabel: string;
    subjectName: string;
    detailLabel: string;
  }): (TimetableEvent & Pick<TimetableDay, 'dayOfWeek'>) | null {
    const startSlot = this.toTimetableSlot(params.startTime);
    const endSlot = this.toTimetableSlot(params.endTime);

    if (startSlot == null || endSlot == null || endSlot <= startSlot) {
      return null;
    }

    const clampedStartSlot = Math.max(0, startSlot);
    const clampedEndSlot = Math.min(TIMETABLE_TOTAL_SLOTS, endSlot);

    if (clampedEndSlot <= clampedStartSlot) {
      return null;
    }

    return {
      dayOfWeek: params.dayOfWeek,
      startSlot: clampedStartSlot,
      endSlot: clampedEndSlot,
      subjectLabel: params.subjectLabel,
      subjectName: params.subjectName,
      detailLabel: params.detailLabel,
    };
  }

  private buildTimetableCells(events: TimetableEvent[]): TimetableCell[] {
    const sortedEvents = events
      .filter((event) => event.endSlot > 0 && event.startSlot < TIMETABLE_TOTAL_SLOTS)
      .sort((a, b) => a.startSlot - b.startSlot || a.endSlot - b.endSlot);
    const cells: TimetableCell[] = [];
    let slot = 0;

    while (slot < TIMETABLE_TOTAL_SLOTS) {
      let nextEvent: TimetableEvent | undefined;

      for (const event of sortedEvents) {
        if (event.startSlot >= slot) {
          nextEvent = event;
          break;
        }
      }

      if (nextEvent == null) {
        this.addEmptyTimetableCells(cells, slot, TIMETABLE_TOTAL_SLOTS);
        break;
      }

      if (nextEvent.startSlot > slot) {
        this.addEmptyTimetableCells(cells, slot, nextEvent.startSlot);
        slot = nextEvent.startSlot;
        continue;
      }

      const group = this.collectOverlappingEvents(sortedEvents, slot);
      cells.push({
        colSpan: group.endSlot - slot,
        events: group.events,
      });
      slot = group.endSlot;
    }

    return cells;
  }

  private addEmptyTimetableCells(cells: TimetableCell[], startSlot: number, endSlot: number): void {
    let currentSlot = startSlot;

    while (currentSlot < endSlot) {
      const nextHourSlot = Math.ceil((currentSlot + 1) / TIMETABLE_SLOTS_PER_HOUR) * TIMETABLE_SLOTS_PER_HOUR;
      const nextSlot = Math.min(nextHourSlot, endSlot);

      cells.push({ colSpan: nextSlot - currentSlot, events: [] });
      currentSlot = nextSlot;
    }
  }

  private collectOverlappingEvents(
    sortedEvents: TimetableEvent[],
    startSlot: number
  ): { endSlot: number; events: TimetableEvent[] } {
    let endSlot = startSlot;
    const events: TimetableEvent[] = [];
    let hasChange = true;

    while (hasChange) {
      hasChange = false;

      for (const event of sortedEvents) {
        const isOverlapping = event.startSlot < endSlot || (endSlot === startSlot && event.startSlot === startSlot);

        if (!isOverlapping || event.endSlot <= startSlot || events.includes(event)) {
          continue;
        }

        events.push(event);
        endSlot = Math.max(endSlot, event.endSlot);
        hasChange = true;
      }
    }

    return {
      endSlot: Math.min(endSlot, TIMETABLE_TOTAL_SLOTS),
      events,
    };
  }

  private toTimetableSlot(value: string): number | null {
    const match = /T(\d{2}):(\d{2})/.exec(value);

    if (match == null) {
      return null;
    }

    const hour = Number(match[1]);
    const minute = Number(match[2]);
    const minutesFromStart = ((hour - TIMETABLE_START_HOUR) * 60) + minute;

    return Math.floor(minutesFromStart / TIMETABLE_SLOT_MINUTES);
  }

  private formatSubjectLabel(code: string, section: string): string {
    return `${code} (${section})`;
  }

  private formatSubjectTooltip(subjectName: string | null | undefined, code: string, section: string): string {
    const normalizedSubjectName = String(subjectName ?? '').trim();

    return normalizedSubjectName === '' ? this.formatSubjectLabel(code, section) : normalizedSubjectName;
  }

  private loadDropdownOptionsForLeaveType(scheduleSubmissionId: number, leaveTypeId: number): void {
    const requestId = ++this.dropdownOptionsRequestId;

    this.scheduleSubmissionService
      .getMakeupTeachingDropdownOptions(scheduleSubmissionId, {
        makeup_teaching_leave_type_id: leaveTypeId,
      })
      .subscribe({
        next: (dropdownOptions) => {
          if (requestId !== this.dropdownOptionsRequestId) {
            return;
          }

          this.applyDropdownOptions(dropdownOptions, false);
          this.enableControlsForRules(dropdownOptions.rules);
          this.applyForcedLeavePeriod(dropdownOptions.rules);

          if (this.buildDropdownOptionsParams() != null) {
            this.reloadDropdownOptionsForLeaveContext();
          }

          queueMicrotask(() => this.resetLeaveTypeDependentInteractionState());
        },
        error: (err) => console.error(err),
      });
  }

  private reloadDropdownOptionsForLeaveContext(): void {
    const scheduleSubmissionId = this.scheduleSubmissionId;
    const params = this.buildDropdownOptionsParams();

    if (scheduleSubmissionId == null) {
      return;
    }

    this.form.get('subjectId')?.enable({ emitEvent: false });

    if (params == null) {
      this.dropdownOptionsRequestId += 1;
      this.lastLeaveContextDropdownOptionsKey = '';
      return;
    }

    const paramsKey = this.getDropdownOptionsParamsKey(params);

    if (paramsKey === this.lastLeaveContextDropdownOptionsKey) {
      return;
    }

    this.lastLeaveContextDropdownOptionsKey = paramsKey;

    const requestId = ++this.dropdownOptionsRequestId;

    this.scheduleSubmissionService
      .getMakeupTeachingDropdownOptions(scheduleSubmissionId, params)
      .subscribe({
        next: (dropdownOptions) => {
          if (requestId !== this.dropdownOptionsRequestId) {
            return;
          }

          this.applyDropdownOptions(dropdownOptions, false);
          this.enableControlsForRules(dropdownOptions.rules);
          this.applyForcedLeavePeriod(dropdownOptions.rules);
          queueMicrotask(() => this.resetLeaveTypeDependentInteractionState());
        },
        error: (err) => {
          if (requestId === this.dropdownOptionsRequestId) {
            this.lastLeaveContextDropdownOptionsKey = '';
          }

          console.error(err);
        },
      });
  }

  private buildDropdownOptionsParams(): MakeupTeachingDropdownOptionsParams | null {
    const formValue = this.form.getRawValue();
    const leaveTypeId = formValue.leaveType;

    if (leaveTypeId == null) {
      return null;
    }

    if (formValue.timeRange == null) {
      return null;
    }

    const params: MakeupTeachingDropdownOptionsParams = {
      makeup_teaching_leave_type_id: leaveTypeId,
      makeup_teaching_leave_period_id: formValue.timeRange,
    };

    if (this.requiresAcademicHoliday) {
      if (formValue.holidayId == null) {
        return null;
      }

      params.academic_holiday_id = formValue.holidayId;
      return params;
    }

    if (this.requiresManualLeaveDate) {
      const leaveDate = this.toApiDate(formValue.leaveDate);

      if (leaveDate == null) {
        return null;
      }

      params.leave_date = leaveDate;
      return params;
    }

    return params;
  }

  private getDropdownOptionsParamsKey(params: MakeupTeachingDropdownOptionsParams): string {
    return [
      params.makeup_teaching_leave_type_id ?? '',
      params.leave_date ?? '',
      params.academic_holiday_id ?? '',
      params.makeup_teaching_leave_period_id ?? '',
    ].join('|');
  }

  private applyDropdownOptions(
    dropdownOptions: MakeupTeachingDropdownOptionsResponse,
    includeLeaveTypes: boolean
  ): void {
    if (includeLeaveTypes) {
      this.leaveTypeOptions.set(
        dropdownOptions.leave_types.map((item) => ({
          ...item,
          name: item.name || item.label,
        }))
      );
    }

    this.dropdownRules.set(dropdownOptions.rules);
    this.syncLeaveDateFieldModeFromRules(dropdownOptions.rules);
    this.holidays.set(
      dropdownOptions.academic_holidays.map((item) => this.toHolidayOption(item))
    );
    this.timeRangeOptions.set(
      dropdownOptions.leave_periods.map((item) => ({
        ...item,
        name: item.label,
      }))
    );
    this.compensatoryTeachingTime.set(
      dropdownOptions.makeup_times.map((item) => this.toMakeupTimeOption(item))
    );
    this.form.updateValueAndValidity({ emitEvent: false });
    const subjectOptions = dropdownOptions.subjects ?? dropdownOptions.subject_schedule_groups;
    this.subjectScheduleOptions.set(
      this.dedupeSubjectOptionsByCode(subjectOptions).map((item) => ({
        ...item,
        optionLabel: this.formatSubjectOptionLabel(item),
      }))
    );
  }

  /**
   * A subject code can map to several `Subject` variant rows (same code/name,
   * different curriculum/study-mode), which surfaces as duplicate entries in the
   * ชื่อรายวิชา dropdown. Users only care about the subject, so keep one option
   * per subject code — the first, honouring the backend's code/courseunit/id
   * ordering. Entries without a code are kept as-is (nothing to dedupe against).
   */
  private dedupeSubjectOptionsByCode(
    options: MakeupTeachingSubjectScheduleGroupOption[]
  ): MakeupTeachingSubjectScheduleGroupOption[] {
    const seenCodes = new Set<string>();

    return options.filter((option) => {
      const code = option.code.trim();

      if (code === '') {
        return true;
      }

      if (seenCodes.has(code)) {
        return false;
      }

      seenCodes.add(code);
      return true;
    });
  }

  private setSelectableDateRange(academicTerm: AuthUserResponse['academic_term']): void {
    const earliestMakeupDate = this.earliestMakeupDate();
    const termStart = this.parseLocalDate(academicTerm?.study_start_date);
    const termEnd = this.parseLocalDate(academicTerm?.study_end_date);
    const minDate = this.maxDate(earliestMakeupDate, termStart);

    this.leaveMinSelectableDate.set(termStart == null ? null : this.toApiDateFromDate(termStart));
    this.minSelectableDate.set(
      minDate == null ? this.toApiDateFromDate(earliestMakeupDate) : this.toApiDateFromDate(minDate)
    );
    this.maxSelectableDate.set(termEnd == null ? null : this.toApiDateFromDate(termEnd));
    this.form.get('compensationDate')?.updateValueAndValidity({ emitEvent: false });
  }

  private earliestMakeupDate(): Date {
    const today = this.startOfLocalDay(new Date());

    return new Date(today.getFullYear(), today.getMonth(), today.getDate() + MAKEUP_REQUEST_LEAD_DAYS);
  }

  private formatSubjectOptionLabel(subject: MakeupTeachingSubjectScheduleGroupOption): string {
    const name = subject.name.trim();
    const code = subject.code.trim();

    if (name === '') {
      return code;
    }

    return code === '' ? name : `${name}(${code})`;
  }

  private parseLocalDate(value: string | null | undefined): Date | null {
    if (value == null || value === '') {
      return null;
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

    if (match == null) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : this.startOfLocalDay(date);
    }

    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  private startOfLocalDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private maxDate(first: Date | null, second: Date | null): Date | null {
    if (first == null) return second;
    if (second == null) return first;

    return first.getTime() >= second.getTime() ? first : second;
  }

  private toApiDateFromDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private setLeaveDateFieldModeForLeaveType(leaveTypeId: number | null): void {
    if (leaveTypeId == null) {
      this.leaveDateFieldMode.set('empty');
      return;
    }

    const selectedLeaveType = this.leaveTypeOptions().find((item) => item.id === leaveTypeId);
    const usesAcademicHoliday = selectedLeaveType?.uses_academic_holiday === true;

    this.leaveDateFieldMode.set(usesAcademicHoliday ? 'holiday' : 'manual');
  }

  private syncLeaveDateFieldModeFromRules(rules: MakeupTeachingDropdownRules): void {
    if (this.selectedLeaveTypeId == null) {
      this.leaveDateFieldMode.set('empty');
      return;
    }

    if (rules.requires_academic_holiday) {
      this.leaveDateFieldMode.set('holiday');
      return;
    }

    if (rules.requires_manual_leave_date) {
      this.leaveDateFieldMode.set('manual');
      return;
    }

    this.leaveDateFieldMode.set('empty');
  }

  private applyForcedLeavePeriod(rules: MakeupTeachingDropdownRules): void {
    if (rules.force_leave_period_code == null) {
      return;
    }

    const forcedLeavePeriodId = this.timeRangeOptions()
      .find((item) => item.code === rules.force_leave_period_code)?.id ?? null;

    const timeRangeControl = this.form.get('timeRange');

    if (timeRangeControl?.value === forcedLeavePeriodId) {
      return;
    }

    timeRangeControl?.setValue(forcedLeavePeriodId, { emitEvent: false });
  }

  private toMakeupTimeOption(item: MakeupTeachingTimeOption): SelectOption {
    return {
      id: item.id,
      name: item.label,
      timeValue: item.time_value,
    };
  }

}
